import React from 'react';
import { Device } from '../../lib/types';
import { formatTimestamp, formatDaysAgo } from '../../lib/utils';
import Button from '../../components/Button/Button';
import BatteryIndicator from './BatteryIndicator';
import { SkeletonRows } from './SkeletonRow';
import Pagination from '../../components/Pagination/Pagination';
import styles from './Devices.module.scss';

const columns = ['Adopter', 'Pet', 'Device', 'Status', 'Battery', 'Transferred', 'Action'];

interface PendingTransfersTableProps {
  devices: Device[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  loading: boolean;
  skeletonCount: number;
  onResendEmail: (device: Device) => void;
  onCancelTransfer: (device: Device) => void;
  onSendSms: (device: Device) => void;
}

export default function PendingTransfersTable({
  devices,
  totalCount,
  currentPage,
  totalPages,
  pageSize,
  onPageChange,
  loading,
  skeletonCount,
  onResendEmail,
  onCancelTransfer,
  onSendSms,
}: PendingTransfersTableProps) {
  if (totalCount === 0 && !loading && skeletonCount === 0) {
    return null;
  }

  const hasData = devices.length > 0;
  const showOverlay = skeletonCount > 0 && hasData;
  const showSkeleton = skeletonCount > 0 && !hasData;
  const wrapperClass = `${styles.tableWrapper}${showOverlay ? ` ${styles.tableTransitioning}` : ''}`;

  return (
    <div className={`${styles.sectionCard} ${styles.sectionCardWarning}`}>
      <div className={styles.sectionHeader}>
        <h3 className={styles.sectionTitle}>Pending Transfers ({totalCount})</h3>
        <p className={styles.sectionDescription}>
          Devices being transferred to an adopter &mdash; waiting for them to activate.
        </p>
      </div>
      <div className={wrapperClass}>
        <table className={styles.table}>
          <colgroup>
            <col style={{ width: '16%' }} /> {/* Adopter */}
            <col style={{ width: '12%' }} /> {/* Pet */}
            <col style={{ width: '14%' }} /> {/* Device */}
            <col style={{ width: '12%' }} /> {/* Status */}
            <col style={{ width: '10%' }} /> {/* Battery */}
            <col style={{ width: '12%' }} /> {/* Transferred */}
            <col style={{ width: '24%' }} /> {/* Action */}
          </colgroup>
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {showSkeleton ? (
              <SkeletonRows count={skeletonCount} columns={columns.length} />
            ) : (
              devices.map((d) => {
                const isOnline = d.status === 'online';

                return (
                  <tr key={d.id}>
                    <td>
                      {d.adopterName}
                      <br />
                      <span className={styles.adopterEmail}>{d.adopterEmail}</span>
                    </td>
                    <td>{d.petName || '\u2014'}</td>
                    <td>
                      <div className={styles.deviceId}>{d.id}</div>
                      <div className={styles.deviceType}>{d.type}</div>
                    </td>
                    <td>
                      {isOnline ? (
                        <>
                          <div className={styles.statusCell}>
                            <span className={`${styles.statusDot} ${styles.statusOnline}`} />
                            Online
                          </div>
                          <div className={styles.lastSeen}>Now</div>
                        </>
                      ) : (
                        <a
                          href="https://support.fitracking.com/hc/en-us/articles/360033210214-Offline-Fi-Device-Troubleshooting"
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Troubleshooting: Offline Fi Device"
                          className={styles.offlineLink}
                        >
                          <div className={styles.statusCell}>
                            <span className={`${styles.statusDot} ${styles.statusOffline}`} />
                            Offline
                          </div>
                          <div className={styles.lastSeen}>{formatTimestamp(d.lastSeen)}</div>
                        </a>
                      )}
                    </td>
                    <td>
                      <BatteryIndicator battery={d.battery} />
                    </td>
                    <td>{formatDaysAgo(d.transferDate)}</td>
                    <td>
                      <div className={styles.actionGroup}>
                        <Button variant="ghost" size="sm" onClick={() => onResendEmail(d)}>
                          Email
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => onSendSms(d)}>
                          Send SMS
                        </Button>
                        <Button variant="danger" size="sm" onClick={() => onCancelTransfer(d)}>
                          Cancel
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className={styles.paginationWrapper}>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalCount}
            pageSize={pageSize}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </div>
  );
}
